import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Heart, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { UniversalSymptomTracker } from '@/components/mental/UniversalSymptomTracker';
import { useUniversalTracking } from '@/hooks/useUniversalTracking';

interface MemoryProcessingExerciseProps {
  onBack: () => void;
}

type ExerciseStep = 'symptoms-before' | 'processing' | 'symptoms-after' | 'complete';

export const MemoryProcessingExercise = ({ onBack }: MemoryProcessingExerciseProps) => {
  const [step, setStep] = useState<ExerciseStep>('symptoms-before');
  const [symptomsBefore, setSymptomsBefore] = useState<Record<string, number>>({});
  const [symptomsAfter, setSymptomsAfter] = useState<Record<string, number>>({});
  const [startTime] = useState<number>(Date.now());
  const { logExercise, isLogging } = useUniversalTracking();
  
  const [currentMemory, setCurrentMemory] = useState('');
  const [responses, setResponses] = useState({
    sensoryDetails: '',
    emotionsPresent: '',
    whatItMeant: '',
    gratitudeForMemory: '',
    howItShapedYou: ''
  });

  const [memoryType, setMemoryType] = useState<'beautiful' | 'painful' | 'complicated' | null>(null);

  const handleComplete = async () => {
    const duration = Math.round((Date.now() - startTime) / 1000 / 60);
    
    try {
      await logExercise({
        exercise_type: 'memory_processing',
        exercise_name: 'Memory Processing',
        category: 'grief',
        duration_minutes: duration,
        symptoms_before: symptomsBefore,
        symptoms_after: symptomsAfter,
        completion_data: { memoryType }
      });
      setStep('complete');
    } catch (error) {
      console.error('Error logging exercise:', error);
      toast.error('Failed to save exercise');
    }
  };

  const handleSave = () => {
    toast.success('Your memory has been processed and saved', {
      description: 'You can return to process more memories anytime'
    });
    setStep('symptoms-after');
  };

  // Symptoms Before Step
  if (step === 'symptoms-before') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Button variant="outline" onClick={onBack} className="mb-4">
          ← Back to Grief Tools
        </Button>

        <Card className="bg-gradient-therapeutic border-primary/20">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-primary/10">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl font-light">Memory Processing</CardTitle>
                <CardDescription>
                  Gently process memories-the beautiful, painful, and complicated
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <Card className="bg-gradient-calm border-border/50">
              <CardContent className="py-4 space-y-3">
                <p className="text-base font-medium">Before We Begin</p>
                <p className="text-sm leading-relaxed">
                  Take a moment to check in with how you're feeling right now. This helps track how the exercise affects your wellbeing.
                </p>
              </CardContent>
            </Card>

            <UniversalSymptomTracker
              mode="before"
              onRatingsChange={setSymptomsBefore}
            />
            
            <Button 
              onClick={() => setStep('processing')} 
              className="w-full"
              disabled={Object.keys(symptomsBefore).length === 0}
            >
              Continue to Memory Processing
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Symptoms After Step
  if (step === 'symptoms-after') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Card className="bg-gradient-therapeutic border-primary/20">
          <CardHeader>
            <CardTitle className="text-xl font-light">How Are You Feeling Now?</CardTitle>
            <CardDescription>
              After processing your memory, take a moment to check in with yourself.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <UniversalSymptomTracker
              mode="after"
              previousRatings={symptomsBefore}
              onRatingsChange={setSymptomsAfter}
            />
            
            <Button 
              onClick={handleComplete} 
              className="w-full"
              disabled={isLogging}
            >
              {isLogging ? 'Saving...' : 'Complete Exercise'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Complete Step
  if (step === 'complete') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Card className="bg-gradient-therapeutic border-primary/20">
          <CardContent className="py-8 text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/20 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-xl font-medium">Memory Processing Complete</h3>
            <p className="text-muted-foreground">
              You've done important work today. Your progress has been tracked and saved.
            </p>
            <Button onClick={onBack} className="mt-4">
              Return to Grief Tools
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Processing Step
  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="outline" onClick={onBack} className="mb-4">
        ← Back to Grief Tools
      </Button>

      <Card className="bg-gradient-therapeutic border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-primary/10">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl font-light">Memory Processing</CardTitle>
              <CardDescription>
                Gently process memories-the beautiful, painful, and complicated
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Introduction */}
          <Card className="bg-gradient-calm border-border/50">
            <CardContent className="py-4 space-y-3">
              <p className="text-sm font-medium">Working With Grief Memories</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                After loss, memories can feel overwhelming-some bring comfort, others bring pain, many bring both. 
                This structured approach, based on <span className="font-medium text-foreground">narrative exposure therapy</span> 
                and <span className="font-medium text-foreground">meaning-making research</span>, helps you process one memory at a time 
                in a way that doesn't re-traumatise but does allow integration.
              </p>
            </CardContent>
          </Card>

          {/* Choose Memory Type */}
          {!memoryType && (
            <div className="space-y-3">
              <label className="text-sm font-medium">What type of memory would you like to process today?</label>
              <div className="grid gap-3 md:grid-cols-3">
                <Card 
                  className="cursor-pointer hover:shadow-md transition-all border-primary/30 hover:border-primary/50"
                  onClick={() => setMemoryType('beautiful')}
                >
                  <CardContent className="py-4 text-center space-y-2">
                    <span className="text-3xl">✨</span>
                    <p className="text-sm font-medium">A Beautiful Memory</p>
                    <p className="text-xs text-muted-foreground">Something precious you want to honour</p>
                  </CardContent>
                </Card>
                <Card 
                  className="cursor-pointer hover:shadow-md transition-all border-soft-accent/30 hover:border-soft-accent/50"
                  onClick={() => setMemoryType('painful')}
                >
                  <CardContent className="py-4 text-center space-y-2">
                    <span className="text-3xl">💔</span>
                    <p className="text-sm font-medium">A Painful Memory</p>
                    <p className="text-xs text-muted-foreground">Something difficult that needs processing</p>
                  </CardContent>
                </Card>
                <Card 
                  className="cursor-pointer hover:shadow-md transition-all border-secondary/30 hover:border-secondary/50"
                  onClick={() => setMemoryType('complicated')}
                >
                  <CardContent className="py-4 text-center space-y-2">
                    <span className="text-3xl">🌓</span>
                    <p className="text-sm font-medium">A Complicated Memory</p>
                    <p className="text-xs text-muted-foreground">Mixed feelings, bittersweet, ambiguous</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {memoryType && (
            <>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  Processing a {memoryType} memory
                </Badge>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setMemoryType(null)}
                  className="text-xs h-7"
                >
                  Change type
                </Button>
              </div>

              {/* The Memory */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Describe the memory</label>
                <p className="text-xs text-muted-foreground">
                  Where were you? What happened? Who was there? Take your time.
                </p>
                <Textarea
                  value={currentMemory}
                  onChange={(e) => setCurrentMemory(e.target.value)}
                  placeholder="I remember when..."
                  className="min-h-[140px] bg-background/80 leading-relaxed"
                />
              </div>

              {/* Sensory Details */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <span className="text-lg">👁️</span>
                  What sensory details do you remember?
                </label>
                <p className="text-xs text-muted-foreground">
                  Sights, sounds, smells, textures, tastes... Sensory details help anchor the memory safely.
                </p>
                <Textarea
                  value={responses.sensoryDetails}
                  onChange={(e) => setResponses({ ...responses, sensoryDetails: e.target.value })}
                  placeholder="The smell of their perfume, the sound of their laugh, the warmth of the sun, the colour of the sky..."
                  className="min-h-[100px] bg-background/80 leading-relaxed"
                />
              </div>

              {/* Emotions */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <span className="text-lg">💧</span>
                  What emotions are present when you recall this?
                </label>
                <p className="text-xs text-muted-foreground">
                  Now, in the present moment, what feelings arise? All are welcome.
                </p>
                <Textarea
                  value={responses.emotionsPresent}
                  onChange={(e) => setResponses({ ...responses, emotionsPresent: e.target.value })}
                  placeholder="Love, sadness, longing, peace, regret, gratitude, anger, tenderness..."
                  className="min-h-[100px] bg-background/80 leading-relaxed"
                />
              </div>

              {/* Meaning */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <span className="text-lg">💎</span>
                  What did this moment mean to you?
                </label>
                <p className="text-xs text-muted-foreground">
                  Why does this memory matter? What does it represent?
                </p>
                <Textarea
                  value={responses.whatItMeant}
                  onChange={(e) => setResponses({ ...responses, whatItMeant: e.target.value })}
                  placeholder="This moment showed me how much they loved me... It was the last time I... It represented..."
                  className="min-h-[100px] bg-background/80 leading-relaxed"
                />
              </div>

              {/* Gratitude or Learning */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <span className="text-lg">🙏</span>
                  {memoryType === 'painful' 
                    ? 'What can you learn from this memory, even if it hurts?'
                    : 'What are you grateful for about this memory?'}
                </label>
                <Textarea
                  value={responses.gratitudeForMemory}
                  onChange={(e) => setResponses({ ...responses, gratitudeForMemory: e.target.value })}
                  placeholder={memoryType === 'painful'
                    ? "This taught me... I survived this... I understand now..."
                    : "I'm grateful this happened, that we had this time, that I can still access this..."}
                  className="min-h-[100px] bg-background/80 leading-relaxed"
                />
              </div>

              {/* How It Shaped You */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <span className="text-lg">🌱</span>
                  How has this memory shaped who you are?
                </label>
                <Textarea
                  value={responses.howItShapedYou}
                  onChange={(e) => setResponses({ ...responses, howItShapedYou: e.target.value })}
                  placeholder="This memory changed how I... It made me more... It influences how I now..."
                  className="min-h-[100px] bg-background/80 leading-relaxed"
                />
              </div>

              {/* R3 Closure */}
              <Card className="bg-gradient-calm border-border/50">
                <CardContent className="py-4 space-y-3">
                  <p className="text-sm font-medium">Completing This Memory</p>
                  <div className="text-xs text-muted-foreground leading-relaxed space-y-2">
                    <p>
                      Take a deep breath. You've just given this memory conscious attention and structure. 
                      The <span className="font-medium text-foreground">R3 Method</span> invites you to:
                    </p>
                    <p>
                      <span className="font-medium text-foreground">Recognise:</span> This memory is part of your story. 
                      It happened. You're here now, recalling it safely.
                    </p>
                    <p>
                      <span className="font-medium text-foreground">Release:</span> You can hold this memory without it 
                      consuming you. It doesn't define you-it's one thread in your tapestry.
                    </p>
                    <p>
                      <span className="font-medium text-foreground">Reprogramme:</span> "I honour this memory. I allow 
                      it to be part of me. I am more than this single moment."
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1">
                  Save as Draft
                </Button>
                <Button className="flex-1" onClick={handleSave}>
                  <Heart className="w-4 h-4 mr-2" />
                  Complete Processing
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* The Science */}
      <Card className="bg-gradient-calm border-border/50">
        <CardContent className="py-4">
          <div className="space-y-3">
            <p className="text-sm font-medium">Why Structured Memory Processing Works</p>
            <div className="text-xs text-muted-foreground leading-relaxed space-y-2">
              <p>
                <span className="font-medium text-foreground">Narrative Exposure Therapy</span> shows that when traumatic 
                or painful memories are fragmented, they intrude unpredictably. By creating a coherent narrative with 
                sensory details, emotions, and meaning, you help your brain file the memory properly-it becomes "a thing 
                that happened" rather than "a thing happening now."
              </p>
              <p>
                <span className="font-medium text-foreground">For beautiful memories:</span> Savouring and elaborating 
                positive memories strengthens neural pathways associated with wellbeing. You're not clinging-you're 
                consolidating something precious.
              </p>
              <p className="text-foreground">
                You can process as many memories as you need, one at a time. Some memories will need multiple passes. 
                That's completely normal. Be gentle with yourself.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
